import GIS from "./app";
import OlOverlay from 'ol/Overlay';
import { Coordinate } from 'ol/coordinate';
import Geometry, {Type} from 'ol/geom/Geometry';
import {Circle, LineString, Polygon} from "ol/geom";
import {unByKey} from 'ol/Observable';
import {EventsKey} from 'ol/events';

export interface TooltipOptions {
    stopEvent?: boolean;
    offset?: [number, number];
}

class Overlay {
    private readonly GIS: GIS;

    private tooltip: {
        overlay: OlOverlay | null;
        element: HTMLDivElement | null;
        stopEvent: boolean;
        offset: [number, number];
    } = {
        overlay: null,
        element: null,
        stopEvent: false,
        offset: [0, -15],
    };

    private centerPoint: {
        overlay: OlOverlay | null;
        element: HTMLDivElement | null;
    } = {
        overlay: null,
        element: null,
    };

    // Geometry the tooltip is currently anchored to, plus the listener that
    // keeps the anchor in sync. A polygon label is placed at the interior
    // point, which shifts as the polygon is reshaped; without tracking the
    // geometry's `change` event the label would stay frozen at its original
    // position after an edit (see #223586).
    private trackedGeometry: Geometry | null = null;
    private geometryChangeKey: EventsKey | null = null;

    constructor(GIS: GIS) {
        this.GIS = GIS;
    }

    /**
     * Initializes the tooltip configuration.
     * @param options - Tooltip configuration options.
     */
    public init(options?: TooltipOptions): void {
        this.tooltip.stopEvent = options?.stopEvent ?? this.tooltip.stopEvent;
        this.tooltip.offset = options?.offset ?? this.tooltip.offset;
    }

    /**
     * Displays a tooltip over the specified geometry.
     * @param geometry - The geometry over which the tooltip is to be displayed.
     * @param text - The text content for the tooltip.
     */
    public show(geometry: Geometry, text: string): void {
        this.createTooltip(geometry);
        this.setTooltipCoordinates(geometry);
        this.tooltip.element!.innerHTML = text;
        this.GIS.Map.olObject!.addOverlay(this.tooltip.overlay!);
        this.trackGeometry(geometry);
    }

    /**
     * Keep the tooltip anchored to the geometry as it changes. Editing a
     * polygon moves its interior point, so the label must recalculate its
     * position on every geometry `change` rather than staying at the anchor it
     * had when {@link show} was first called.
     * @param geometry - The geometry to follow.
     */
    private trackGeometry(geometry: Geometry): void {
        if (this.trackedGeometry === geometry && this.geometryChangeKey) {
            return;
        }
        this.untrackGeometry();
        this.trackedGeometry = geometry;
        this.geometryChangeKey = geometry.on('change', () => {
            if (this.tooltip.overlay) {
                this.setTooltipCoordinates(geometry);
            }
        });
    }

    /**
     * Stop following the currently tracked geometry.
     */
    private untrackGeometry(): void {
        if (this.geometryChangeKey) {
            unByKey(this.geometryChangeKey);
            this.geometryChangeKey = null;
        }
        this.trackedGeometry = null;
    }

    /**
     * Hides and removes the current tooltip and any helper graphics (e.g. the circle center point).
     */
    hide(): void {
        this.untrackGeometry();
        this.tooltip.overlay?.setPosition(undefined); // Removes the overlay from the map without destroying it
        this.tooltip.element?.remove(); // Remove the tooltip element from the DOM
        this.tooltip.element = null;
        this.tooltip.overlay = null;
        this.hideCenterPoint();
    }

    /**
     * Displays a helper center point marker at the given coordinate.
     *
     * Used for circle measurements so the geometric center is clearly visible and the
     * measurement label can be anchored directly above it. The marker is a lightweight
     * DOM overlay (a helper graphic), kept visually distinct from the drawn circle.
     * @param coordinate - Map coordinate of the center point.
     */
    public showCenterPoint(coordinate: Coordinate): void {
        if (!this.centerPoint.overlay) {
            const element = document.createElement('div');
            element.className = 'cfm-center-point';
            this.centerPoint.element = element;
            this.centerPoint.overlay = new OlOverlay({
                element: this.centerPoint.element,
                positioning: 'center-center',
                stopEvent: false,
            });
            this.GIS.Map.olObject!.addOverlay(this.centerPoint.overlay);
        }
        this.centerPoint.overlay.setPosition(coordinate);
    }

    /**
     * Hides and removes the helper center point marker.
     */
    public hideCenterPoint(): void {
        this.centerPoint.overlay?.setPosition(undefined);
        this.centerPoint.element?.remove();
        this.centerPoint.element = null;
        this.centerPoint.overlay = null;
    }

    private createTooltip(geometry?: Geometry): void {
        this.hide();
        const element = document.createElement('div');
        element.className = 'cfm-tooltip';
        this.tooltip.element = element;

        // Circle measurements keep a helper center point marker. Anchor the
        // label to the upper-right of that point (matching the committed circle
        // label) instead of centering it over the point, so the center dot
        // stays clear in the lower-left.
        const isCircle = geometry instanceof Circle;
        this.tooltip.overlay = new OlOverlay({
            element: this.tooltip.element,
            offset: isCircle ? [12, -10] : this.tooltip.offset,
            positioning: isCircle ? 'bottom-left' : 'bottom-center',
            stopEvent: this.tooltip.stopEvent,
        });
    }

    private setTooltipCoordinates(geom: Geometry): void {
        let coordinates: Coordinate | undefined;
        let type: Type;

        try {
            type = geom.getType();
        } catch (e) {
            throw Error('Not valid geometry for a tooltip.')
        }

        switch (type) {
            case 'LineString':
                coordinates = (geom as LineString).getLastCoordinate();
                break;
            case 'Polygon':
                coordinates = (geom as Polygon).getInteriorPoint().getCoordinates();
                break;
            case 'Circle':
                coordinates = (geom as Circle).getCenter();
                break;
            default:
                throw Error('Unknown geometry type for the tooltip.');
        }

        this.tooltip.overlay!.setPosition(coordinates);
    }
}

export default Overlay;